import { call, fork, put, take } from 'redux-saga/effects'
import { bindAsyncAction } from 'typescript-fsa-redux-saga'
import { Action } from 'typescript-fsa'

import { InstagramPostApi, InstagramPageApi } from '../../api/instances'

import {
  deleteInstagramPostComment,
  deleteInstagramPostCommentInitiated,
  getIgPageFeed,
  getIgPageFeedInitiated,
  getInstagramPage, getInstagramPageInitiated,
  writeIgPostComment,
  writeIgPostCommentInitiated,
} from './instagram.actions'

import {
  DeleteInstagramPostCommentRequest,
  GetInstagramPageFeedRequest,
  WriteInstagramPostCommentRequest,
} from '@3m5/sm_publish-frontend-api/apis/InstagramPostApi'
import { GetInstagramPageRequest } from '@3m5/sm_publish-frontend-api/apis/InstagramPageApi'
import { requestFailed, requestFinished, requestStarted } from '../common/common.actions'
import { EventType, Severity } from '@professocial/professocialTypes/types/messageTypes'
import i18n from 'i18next'
import { NetworkType } from '@3m5/sm_publish-frontend-api'

// getInstagramPage
const getInstagramPageWorker = bindAsyncAction(getInstagramPage)(
  function * (params: GetInstagramPageRequest) {
    return yield call([InstagramPageApi, InstagramPageApi.getInstagramPage], params)
  },
)

function * watchGetInstagramPageInitiated () {
  while (true) {
    const action: Action<GetInstagramPageRequest> = yield take(getInstagramPageInitiated)
    try {
      yield getInstagramPageWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetInstagramPageInitiated: ', e)
    }
  }
}

// getIgPageFeed
const getIgPageFeedWorker = bindAsyncAction(getIgPageFeed)(
  function * (params: GetInstagramPageFeedRequest) {
    return yield call([InstagramPostApi, InstagramPostApi.getInstagramPageFeed], params)
  },
)

function * watchGetIgPageFeedInitiated () {
  while (true) {
    const action: Action<{ data: GetInstagramPageFeedRequest, id: string }> = yield take(getIgPageFeedInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.id }))
    try {
      yield getIgPageFeedWorker(action.payload.data)
      yield put(requestFinished({ type: action.type, id: action.payload.id }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.id,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postMessages:loadingError', { context: i18n.t('postCommon:posts') }),
          eventType: EventType.load,
        },
      }))
      console.error('error in watchGetIgPageFeedInitiated: ', e)
    }
  }
}

// writeIgPostComment
const writeIgPostCommentWorker = bindAsyncAction(writeIgPostComment)(
  function * (params: WriteInstagramPostCommentRequest) {
    return yield call([InstagramPostApi, InstagramPostApi.writeInstagramPostComment], params)
  },
)

function * watchWriteIgPostCommentInitiated () {
  while (true) {
    const action: Action<WriteInstagramPostCommentRequest> = yield take(writeIgPostCommentInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.networkPostId }))
    try {
      yield writeIgPostCommentWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        id: action.payload.networkPostId,
        message: {
          severity: Severity.success,
          message: i18n.t('postMessages:isSuccessfullyCreated', { context: i18n.t('postCommon:comment') }),
          eventType: EventType.create,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.networkPostId,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postMessages:createError', { context: i18n.t('postCommon:comment') }),
          eventType: EventType.create,
        },
      }))
      console.error('error in watchWriteIgPostCommentInitiated: ', e)
    }
  }
}

// deletePostComment
const deletePostCommentWorker = bindAsyncAction(deleteInstagramPostComment)(
  function * (params: DeleteInstagramPostCommentRequest) {
    return yield call([InstagramPostApi, InstagramPostApi.deleteInstagramPostComment], params)
  },
)

function * watchDeletePostCommentInitiated () {
  while (true) {
    const action: Action<DeleteInstagramPostCommentRequest> = yield take(deleteInstagramPostCommentInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.commentId, network: NetworkType.Instagram }))
    try {
      yield deletePostCommentWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        id: action.payload.commentId,
        message: {
          severity: Severity.success,
          message: i18n.t('postMessages:isSuccessfullyDeleted', { context: i18n.t('postCommon:comment') }),
          eventType: EventType.delete,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.commentId,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postMessages:deleteError', { context: i18n.t('postCommon:comment') }),
          eventType: EventType.delete,
        },
      }))
      console.error('error in watchDeletePostCommentInitiated: ', e)
    }
  }
}

export function * instagramSagas () {
  yield fork(watchGetInstagramPageInitiated)
  yield fork(watchGetIgPageFeedInitiated)
  yield fork(watchWriteIgPostCommentInitiated)
  yield fork(watchDeletePostCommentInitiated)
}
